import { Copy, Loader2, Sliders } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.js';
import { useLicense } from '../../common/license-context.js';
import { KpiChip } from '../../kpi/components/KpiChip.js';
import {
  useCreateKpiKey,
  useKpiKeys,
  useKpiMetrics,
  useKpiTypes,
  useUpdateKpiKey,
} from '../../kpi/hooks.js';
import { asKpiMetric, asKpiType, type KpiMetric, type KpiType } from '../../kpi/types.js';
import { buildKpiModuleJson } from '../kpi-module-json.js';

const COPY_FAILED = 'Copy failed: clipboard access is blocked by the browser';

/**
 * Copy `text`, preferring the async Clipboard API. The customer app runs
 * inside Workfront iframes, where `navigator.clipboard` is often missing
 * or blocked (no `clipboard-write` permission), so fall back to a hidden
 * textarea + `document.execCommand('copy')` — the same two-step approach as
 * legacy `AttachKPIs`. Resolves false only when both paths fail.
 *
 * The textarea goes into `container` (inside the open popover) rather than
 * `document.body`: focusing an element outside the popover would dismiss it.
 */
async function copyText(text: string, container: HTMLElement): Promise<boolean> {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Blocked by permissions policy — fall through to the legacy path.
    }
  }
  const previouslyFocused = document.activeElement;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  container.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
    if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
  }
}

/**
 * Per-row KPI-attach popover for the scenarios list. Mirrors the
 * legacy `wf-audit/src/slices/scenario/components/scenarioBusiness/AttachKPIs.tsx`
 * flow:
 *
 *   1. Click the `KPIs: N` badge in any scenario row →
 *   2. Popover lists every license metric grouped by KPI type
 *      (with the "General" bucket pinned to the top for metrics
 *      with no type assignment).
 *   3. Checkboxes pre-fill from the scenario's existing `kpi_key`
 *      row (if any). Save creates the key when missing or updates
 *      it when present — both via `useCreateKpiKey` / `useUpdateKpiKey`
 *      which call `POST/PUT /v1/kpi/keys` against the engine.
 *
 * Once the scenario has a key with metrics attached (and the build knows
 * the public KPI intake URL), the footer also offers "Copy JSON": the
 * Fusion `http:ActionSendData` module that posts to that intake with the
 * scenario's key. Keys are random UUIDs, not derived from the scenario id,
 * because the intake is public and the key is the only thing guarding it.
 *
 * The component is intentionally self-contained: the trigger
 * doubles as the visual badge so the existing row layout stays
 * unchanged, just clickable.
 *
 * Operator-reported: "I cannot assign KPI by clicking on KPI in
 * scenarios list, fix it as well". Pre-fix the `KPIs: N` text was
 * a static label.
 */
export interface ScenarioKpiSelectorProps {
  fusionScenarioId: string;
  /** Cached count from the listing row; shown until the metric
   *  hover-popover loads its own data. */
  initialCount: number;
}

export function ScenarioKpiSelector({
  fusionScenarioId,
  initialCount,
}: ScenarioKpiSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { data: metricRows = [], isLoading: metricsLoading } = useKpiMetrics();
  const { data: typeRows = [] } = useKpiTypes();
  const { data: keyRows = [] } = useKpiKeys();
  const create = useCreateKpiKey();
  const update = useUpdateKpiKey();
  const licenseId = useLicense().details?.licenseId;

  const metrics = useMemo<ReadonlyArray<KpiMetric>>(
    () => metricRows.map(asKpiMetric),
    [metricRows],
  );
  const types = useMemo<ReadonlyArray<KpiType>>(() => typeRows.map(asKpiType), [typeRows]);
  const typeById = useMemo(() => {
    const m = new Map<number, KpiType>();
    for (const t of types) m.set(t.id, t);
    return m;
  }, [types]);

  // Existing key for this scenario — engine response shape is
  // `{ fusionScenarioId, metrics: number[], id, key, … }`. The hook
  // type is loose; narrow at the call site.
  const existingKey = useMemo<{
    id: number;
    key: string;
    metricIds: ReadonlyArray<number>;
  } | null>(() => {
    const found = keyRows.find(
      (r) => (r as { fusionScenarioId?: string }).fusionScenarioId === fusionScenarioId,
    );
    if (!found) return null;
    const id = typeof (found as { id?: number }).id === 'number' ? (found as { id: number }).id : 0;
    const attached = Array.isArray((found as { metrics?: unknown }).metrics)
      ? ((found as { metrics: unknown[] }).metrics
          .map((m) => (typeof m === 'number' ? m : Number(m)))
          .filter(Number.isFinite) as number[])
      : [];
    const key =
      typeof (found as { key?: unknown }).key === 'string' ? (found as { key: string }).key : '';
    return { id, key, metricIds: attached };
  }, [keyRows, fusionScenarioId]);

  const currentCount = existingKey?.metricIds.length ?? initialCount;

  // Local checkbox state. Re-seed each time the popover opens so
  // closing without saving discards any unsaved edits.
  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set(existingKey?.metricIds ?? []),
  );
  useEffect(() => {
    if (open) setSelected(new Set(existingKey?.metricIds ?? []));
  }, [open, existingKey?.metricIds]);

  // Group metrics by KPI type; "No type" / unmapped metrics land in
  // a trailing bucket. Sort by type name (ascending) with "No type"
  // pinned last for predictable scanning.
  const grouped = useMemo(() => {
    interface Group {
      key: string;
      type: KpiType | null;
      metrics: KpiMetric[];
    }
    const byType = new Map<number | null, Group>();
    for (const m of metrics) {
      const tid = m.kpiTypeId;
      const key = tid ?? -1;
      let g = byType.get(tid);
      if (!g) {
        g = {
          key: tid !== null ? String(tid) : '__no_type__',
          type: tid !== null ? (typeById.get(tid) ?? null) : null,
          metrics: [],
        };
        byType.set(tid, g);
      }
      g.metrics.push(m);
      void key;
    }
    const sorted = [...byType.values()].sort((a, b) => {
      if (!a.type && b.type) return 1;
      if (a.type && !b.type) return -1;
      return (a.type?.name ?? '').localeCompare(b.type?.name ?? '');
    });
    return sorted;
  }, [metrics, typeById]);

  function toggle(id: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave(): Promise<void> {
    const nextMetrics = [...selected].sort((a, b) => a - b);
    const prevMetrics = [...(existingKey?.metricIds ?? [])].sort((a, b) => a - b);
    const unchanged =
      prevMetrics.length === nextMetrics.length &&
      prevMetrics.every((id, i) => id === nextMetrics[i]);
    if (unchanged) {
      setOpen(false);
      return;
    }
    try {
      if (existingKey) {
        // Engine PUT replaces the attached-set wholesale.
        await update.mutate({ id: existingKey.id, input: { metrics: nextMetrics } });
      } else if (nextMetrics.length > 0) {
        // First-time attachment for this scenario. The key is a random
        // UUID: the public KPI intake accepts a post with just the key
        // and license id, so it must not be guessable (a
        // `scenario-<id>` key was).
        await create.mutate({
          key: crypto.randomUUID(),
          fusionScenarioId,
          metrics: nextMetrics,
        });
      } else {
        // No existing key + no selection — nothing to do.
        setOpen(false);
        return;
      }
      toast.success(
        nextMetrics.length === 0
          ? 'KPI attachments cleared'
          : `KPIs attached: ${nextMetrics.length}`,
      );
      setOpen(false);
    } catch {
      // Hooks surface the error via toast; popover stays open so
      // the operator can retry.
    }
  }

  const saving = create.isPending || update.isPending;

  // Copy JSON targets what is saved on the key (the intake only accepts
  // metrics attached to it), in catalog order, and only when the build
  // knows where the intake lives. While the checkboxes differ from the
  // saved set the module would be stale, so the button is disabled.
  const intakeUrl = import.meta.env.VITE_KPI_INTAKE_URL;
  const copyableMetricNames = useMemo<string[]>(() => {
    const attached = new Set(existingKey?.metricIds ?? []);
    return metrics.flatMap((m) => (attached.has(m.id) && m.internalName ? [m.internalName] : []));
  }, [metrics, existingKey?.metricIds]);
  const canCopyJson =
    !!intakeUrl && !!licenseId && !!existingKey?.key && copyableMetricNames.length > 0;
  const hasUnsavedEdits = useMemo(() => {
    const saved = existingKey?.metricIds ?? [];
    return saved.length !== selected.size || saved.some((id) => !selected.has(id));
  }, [existingKey?.metricIds, selected]);

  async function handleCopyJson(container: HTMLElement): Promise<void> {
    if (!intakeUrl || !licenseId || !existingKey?.key || hasUnsavedEdits) return;
    const copied = await copyText(
      buildKpiModuleJson({
        intakeUrl,
        licenseId,
        key: existingKey.key,
        metricInternalNames: copyableMetricNames,
      }),
      container,
    );
    if (copied) toast.success('JSON copied to clipboard');
    else toast.error(COPY_FAILED);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // `inline-flex` + the same gap/typography as the
          // surrounding metadata row so the trigger looks like the
          // other inline stats. Stop the click from propagating —
          // the row itself opens the detail modal.
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="inline-flex items-center gap-1 rounded font-mono transition-colors hover:bg-foreground/10 hover:text-foreground"
          title="Attach KPI metrics to this scenario"
        >
          <Sliders className="h-3.5 w-3.5" />
          KPIs: {currentCount}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 border-foreground/15 bg-[color:var(--surface-overlay)] p-0 text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-foreground/10 px-3 py-2 text-sm font-semibold">
          Attach KPIs
        </header>

        <div className="max-h-72 overflow-y-auto p-3">
          {metricsLoading ? (
            <div className="flex items-center gap-2 py-3 text-xs text-foreground/55">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading metrics…
            </div>
          ) : grouped.length === 0 ? (
            <p className="py-2 text-xs text-foreground/55">
              No KPI metrics defined yet — head to KPI Builder → KPI Metrics to add some.
            </p>
          ) : (
            grouped.map((g) => (
              <section key={g.key} className="mb-3 last:mb-0">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground/55">
                  {g.type ? g.type.name : 'No type'}
                </h3>
                <ul className="space-y-0.5">
                  {g.metrics.map((m) => {
                    const checked = selected.has(m.id);
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => toggle(m.id)}
                          role="menuitemcheckbox"
                          aria-checked={checked}
                          className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors hover:bg-foreground/10"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            tabIndex={-1}
                            className="h-3.5 w-3.5 accent-[color:var(--brand-yellow)]"
                          />
                          <KpiChip icon={m.icon} color={m.color} size="sm" />
                          <span className="flex-1 truncate text-foreground/90">
                            {m.displayName}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-foreground/10 px-3 py-2">
          {canCopyJson ? (
            // A disabled button has `pointer-events: none`, so it never shows
            // its own `title`: the stale-edits hint goes on the wrapper.
            <span
              title={hasUnsavedEdits ? 'Save changes before copying' : undefined}
              className="mr-auto inline-flex"
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => void handleCopyJson(e.currentTarget.parentElement ?? document.body)}
                disabled={hasUnsavedEdits}
                title={
                  hasUnsavedEdits
                    ? undefined
                    : "Copy the Fusion module that posts to this scenario's KPI key"
                }
                className="h-7 px-2 text-xs text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
              >
                <Copy className="mr-1 h-3.5 w-3.5" />
                Copy JSON
              </Button>
            </span>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={saving}
            className="h-7 px-2 text-xs text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={saving}
            className="h-7 bg-[color:var(--brand-yellow)] px-3 text-xs text-[color:var(--gradient-end)] hover:opacity-90"
          >
            {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </Button>
        </footer>
      </PopoverContent>
    </Popover>
  );
}
